import { formatInTimeZone } from "date-fns-tz";
import { Calendar, Clock, MessageSquare, UserCheck } from "lucide-react";
import { getLocationIcon, isConfrenceType } from "./location-icon";
import { Booking } from "@/types/booking";
import { cn } from "@/lib/utils";
import { Location, LocationType } from "@/types/location";

const statusDetails: Record<string, { label: string; color: string }> = {
  processing: {
    label: "Processing",
    color: "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  },
  pending: {
    label: "Pending Approval",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100",
  },
  waiting: {
    label: "Waiting for Payment",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100",
  },
  confirmed: {
    label: "Confirmed",
    color: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  },
  canceled: {
    label: "Cancelled",
    color: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100",
  },
  completed: {
    label: "Completed",
    color:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100",
  },
  noshow: {
    label: "No Show",
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
};

export function BookingInfo({ booking }: { booking: Booking }) {
  const invitee = booking.invitees ? booking.invitees[0] : undefined;
  if (!invitee) return;

  const location: Location = booking.location;
  const statusInfo = statusDetails[invitee.status];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 justify-start text-left">
        <div className="w-5 flex-shrink-0">
          <UserCheck className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="capitalize">{booking.subject?.split(":")[0]}</span>
          {statusInfo && (
            <span
              className={cn(
                "text-xs font-medium px-2 py-1 rounded-xl",
                statusInfo.color
              )}
            >
              {statusInfo.label}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Calendar className="w-5 h-5 text-muted-foreground" />
        <span className={cn({ "line-through": invitee.status === "canceled" })}>
          {formatInTimeZone(
            new Date(booking.start_at),
            invitee.time_zone,
            "EEEE, MMMM d, yyyy"
          )}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <Clock className="w-5 h-5 text-muted-foreground" />
        <span className={cn({ "line-through": invitee.status === "canceled" })}>
          {formatInTimeZone(
            new Date(booking.start_at),
            invitee.time_zone,
            "h:mm a"
          )}{" "}
          -{" "}
          {formatInTimeZone(
            new Date(booking.end_at),
            invitee.time_zone,
            "h:mm a (zzz)"
          )}
        </span>
      </div>

      {invitee.status === "confirmed" && (
        <div className="flex items-center gap-3">
          {getLocationIcon(location.type as LocationType)}
          <div className="flex-1 min-w-0 text-left">
            <span>
              {location.name === "Ask Invitee" ? "Location" : location.name}
            </span>
            {/* Show phone and remarks if type is phone */}
            {location.type === "phone" && (
              <>
                {location.phone && (
                  <div className="text-sm">
                    <a
                      href={`tel:${location.phone}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {location.phone}
                    </a>
                  </div>
                )}
                {location.remarks && (
                  <div className="text-xs text-muted-foreground">
                    {location.remarks}
                  </div>
                )}
              </>
            )}
            {!location.join_url &&
              (location.type === "physical" ||
                location.type === "ask_invitee") && (
                <div className="space-y-1 text-muted-foreground text-sm">
                  {location.address && <div>{location.address}</div>}
                  {location.city && (
                    <div>
                      {location.city}, {location.state}
                    </div>
                  )}
                  {location.zip_code && (
                    <div>
                      {location.zip_code}, {location.country}
                    </div>
                  )}
                  {location.phone && (
                    <div>
                      {location.phone}
                      {location.email && `, ${location.email}`}
                    </div>
                  )}
                </div>
              )}
            {isConfrenceType(location.type as LocationType) &&
              !location.join_url && (
                <div className="text-sm text-muted-foreground">
                  Joining link will be sent via email.
                </div>
              )}
            {location.join_url && (
              <div className="min-w-0 max-w-full overflow-hidden">
                <a
                  className="text-sm text-blue-600 hover:text-blue-800 break-all line-clamp-1"
                  href={location.join_url}
                  target="_blank"
                  title={location.join_url}
                >
                  {location.join_url}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {!["confirmed", "completed"].includes(invitee.status) &&
        invitee.reason && (
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
            <span className="text-destructive">{invitee.reason}</span>
          </div>
        )}
    </div>
  );
}
